import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const ServiceSelector = ({ 
  value, 
  onChange, 
  disabled = false,
  error = null,
  services: externalServices = null, // Servicios pasados desde el padre
  compact = false, // Modo compacto para formularios de items
  showSku = true, // Mostrar SKU en el selector
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [services, setServices] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);

  // Si se pasan servicios externos, usarlos
  useEffect(() => {
    if (externalServices !== null) {
      setServices(externalServices);
    }
  }, [externalServices]);

  // Posicionar dropdown (para portal) cuando se abre — fixed respecto al viewport
  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 220),
    });
  }, [isOpen]);

  // Cerrar dropdown al hacer clic fuera (trigger o contenido del portal)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = triggerRef.current && triggerRef.current.contains(event.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      if (!inTrigger && !inDropdown) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filtrar servicios según búsqueda
  const filteredServices = useMemo(() => {
    if (!searchTerm.trim()) {
      return services;
    }
    
    const search = searchTerm.toLowerCase();
    return services.filter(service => {
      const nameMatch = service.name?.toLowerCase().includes(search);
      const skuMatch = showSku && service.sku?.toLowerCase().includes(search);
      const typeMatch = service.service_type?.toLowerCase().includes(search);
      
      return nameMatch || skuMatch || typeMatch;
    });
  }, [services, searchTerm]);

  // Obtener el servicio seleccionado
  const selectedService = useMemo(() => {
    if (!value) return null;
    return services.find(s => s.id === parseInt(value));
  }, [services, value]);

  const handleSelect = (service) => {
    onChange(service.id);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex(filteredServices.length > 0 ? 0 : -1);
  }, [isOpen, searchTerm, filteredServices.length]);

  useEffect(() => {
    if (highlightedIndex < 0) return;
    const el = optionRefs.current[highlightedIndex];
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightedIndex, filteredServices]);

  const handleInputKeyDown = (event) => {
    if (disabled) return;
    const { key } = event;
    const count = filteredServices.length;

    if (key === 'ArrowDown') {
      event.preventDefault();
      if (selectedService) onChange('');
      if (!isOpen) {
        setIsOpen(true);
        setHighlightedIndex(count > 0 ? 0 : -1);
        return;
      }
      if (count === 0) return;
      setHighlightedIndex((prev) => (prev < count - 1 ? prev + 1 : 0));
      return;
    }

    if (key === 'ArrowUp') {
      event.preventDefault();
      if (selectedService) onChange('');
      if (!isOpen) {
        setIsOpen(true);
        setHighlightedIndex(count > 0 ? count - 1 : -1);
        return;
      }
      if (count === 0) return;
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : count - 1));
      return;
    }

    if (key === 'Enter') {
      if (!isOpen || highlightedIndex < 0 || !filteredServices[highlightedIndex]) return;
      event.preventDefault();
      handleSelect(filteredServices[highlightedIndex]);
      return;
    }

    if (key === 'Escape' && isOpen) {
      event.preventDefault();
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const dropdownContent = isOpen && !disabled && (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-y-auto"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
      }}
    >
      {filteredServices.length === 0 ? (
        <div className="px-4 py-3 text-sm text-gray-500 text-center">
          {searchTerm ? 'No se encontraron servicios' : 'No hay servicios disponibles'}
        </div>
      ) : (
        <div className="py-1">
          {filteredServices.map((service, index) => {
            const isHighlighted = highlightedIndex === index;
            const isSelected = selectedService?.id === service.id;
            return (
            <button
              key={service.id}
              ref={(el) => {
                optionRefs.current[index] = el;
              }}
              type="button"
              role="option"
              aria-selected={isHighlighted || isSelected}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => handleSelect(service)}
              className={`w-full text-left px-4 py-2 transition-colors ${
                isHighlighted
                  ? 'bg-teal-100 ring-1 ring-inset ring-teal-400'
                  : isSelected
                    ? 'bg-teal-50'
                    : 'hover:bg-teal-50'
              }`}
            >
              <div className="font-medium text-gray-900">{service.name}</div>
              {showSku && (
                <div className="text-xs text-gray-500 mt-0.5">
                  <span className="font-medium">SKU:</span> {service.sku}
                  {service.service_type && (
                    <>
                      {' • '}
                      <span className="font-medium">Tipo:</span> {service.service_type === 'acquisition' ? 'Adquisición' : 'Venta'}
                    </>
                  )}
                </div>
              )}
            </button>
          );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="relative" ref={triggerRef}>
      {/* Input de búsqueda */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={selectedService ? (showSku ? `${selectedService.name} - ${selectedService.sku}` : selectedService.name) : searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
            if (selectedService) {
              onChange('');
            }
          }}
          onKeyDown={handleInputKeyDown}
          onFocus={() => setIsOpen(true)}
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          placeholder={showSku ? "Buscar servicio por SKU, nombre o tipo..." : "Buscar servicio por nombre..."}
          disabled={disabled}
          className={`w-full ${compact ? 'px-2 py-1.5 text-sm' : 'px-4 py-2.5'} border ${compact ? 'rounded-md focus:ring-1' : 'rounded-lg focus:ring-2'} focus:outline-none transition-all pr-10 ${
            error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        />
        <button
          type="button"
          onClick={() => {
            if (!disabled) {
              setIsOpen(!isOpen);
              if (!isOpen) setSearchTerm('');
            }
          }}
          disabled={disabled}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Dropdown vía portal para que no quede recortado por overflow en documentos */}
      {typeof document !== 'undefined' && createPortal(dropdownContent, document.body)}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default ServiceSelector;



